/**
 * OS fills the new-entry page (web-redesign-10 Decided 6, phase 7).
 *
 * While the person has a new entry open, what they tell OS ("two glucometer
 * strips for Sita Devi, she paid on UPI") goes into the form in front of
 * them, not onto a card: the `fill_entry` tool answers a fill, and the page
 * puts it in, marking each field it filled. Nothing is saved: Save stays the
 * person's.
 *
 * The values are already the form's own (a choice's option id, a list row's
 * id, a day as YYYY-MM-DD, a number), resolved and checked on the server
 * against the schema this person is served, so the page puts them in with no
 * guessing.
 *
 * `saw` is what the page showed OS for each field it fills (the view's draft
 * words, cut as the view cuts them), so the page can tell a field the person
 * changed after OS last looked: that one is theirs, and the fill leaves it.
 */
import { LINES_MAX_DEPTH, LINES_MAX_PER_ENTRY, type EntryLinesWrite, type EntryValue, type LineWrite } from './api';
import { agentViewWords } from './agent-view';

export type AgentEntryFill = {
  /** The record whose new entry this fills. */
  readonly recordId: string;
  /** The values OS puts in, keyed by field id. Null empties a field. */
  readonly values: Readonly<Record<string, EntryValue>>;
  /** The title of each list row a value names, so the picker can draw it. */
  readonly titles: Readonly<Record<string, string>>;
  /** What OS was shown in each field it fills, by field id: the draft's words, or null when it showed nothing there. */
  readonly saw: Readonly<Record<string, string | null>>;
  /**
   * The fields it fills that OS was never shown: the draft it read was cut
   * (`AGENT_VIEW_DRAFT_MAX`) before them, so `saw` cannot say what was there.
   * The page puts these in: the person has just said them. Absent when OS saw
   * every one.
   */
  readonly unseen?: readonly string[];
  /**
   * The lines OS heard, by `lines` field id, typed values only, a depth-1
   * line holding its own (Decision 5 of docs/plans/entry-lines-and-layout.md).
   * The page adds them to the runs it draws. Absent when it heard none.
   */
  readonly lines?: EntryLinesWrite;
};

/** What `fill_entry` answers: the fill, for the page, and its words, for the model's one line. */
export type FillOutput = { readonly kind: 'fill'; readonly fill: AgentEntryFill; readonly filled: readonly string[] };

/** The most fields one fill carries. */
export const AGENT_FILL_FIELDS_MAX = 40;

const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const FIELD_ID = /^[A-Za-z0-9_.:-]{1,120}$/;

const isObject = (raw: unknown): raw is Record<string, unknown> => typeof raw === 'object' && raw !== null && !Array.isArray(raw);

const valueOf = (raw: unknown): EntryValue | undefined => {
  if (raw === null) return null;
  if (typeof raw === 'string') return raw.length <= 4000 ? raw : undefined;
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : undefined;
  if (typeof raw === 'boolean') return raw;
  if (Array.isArray(raw) && raw.length <= 50 && raw.every((one) => typeof one === 'string' && one.length <= 200)) return raw as string[];
  return undefined;
};

/**
 * The lines of a fill, read loosely: a line with no uuid is dropped, a value
 * it cannot read is dropped, and no more than LINES_MAX_PER_ENTRY lines are
 * kept in all. Undefined when none are left.
 */
const linesOf = (raw: unknown, depth: number, budget: { left: number }): EntryLinesWrite | undefined => {
  if (!isObject(raw)) return undefined;
  const out: Record<string, readonly LineWrite[]> = {};
  for (const [fieldId, run] of Object.entries(raw)) {
    if (!ID.test(fieldId) || !Array.isArray(run)) continue;
    const kept: LineWrite[] = [];
    for (const line of run) {
      if (budget.left <= 0) break;
      if (!isObject(line) || typeof line.entryId !== 'string' || !ID.test(line.entryId) || !isObject(line.values)) continue;
      const values: Record<string, EntryValue> = {};
      for (const [lineFieldId, held] of Object.entries(line.values)) {
        if (!FIELD_ID.test(lineFieldId)) continue;
        const value = valueOf(held);
        if (value !== undefined) values[lineFieldId] = value;
      }
      budget.left -= 1;
      const below = depth < LINES_MAX_DEPTH ? linesOf(line.lines, depth + 1, budget) : undefined;
      kept.push({ entryId: line.entryId.toLowerCase(), values, ...(below === undefined ? {} : { lines: below }) });
    }
    if (kept.length > 0) out[fieldId] = kept;
  }
  return Object.keys(out).length === 0 ? undefined : out;
};

/**
 * A fill, read loosely off a tool part, or null when it is not one. A field
 * whose value it cannot read is dropped, never the whole fill; a fill with no
 * record, or nothing to put in, is null.
 */
export const readAgentEntryFill = (raw: unknown): AgentEntryFill | null => {
  if (!isObject(raw) || typeof raw.recordId !== 'string' || !ID.test(raw.recordId) || !isObject(raw.values)) return null;
  const values: Record<string, EntryValue> = {};
  for (const [fieldId, held] of Object.entries(raw.values)) {
    if (Object.keys(values).length >= AGENT_FILL_FIELDS_MAX) break;
    if (!FIELD_ID.test(fieldId)) continue;
    const value = valueOf(held);
    if (value !== undefined) values[fieldId] = value;
  }
  const lines = linesOf(raw.lines, 1, { left: LINES_MAX_PER_ENTRY });
  if (Object.keys(values).length === 0 && lines === undefined) return null;
  const titles: Record<string, string> = {};
  if (isObject(raw.titles)) {
    for (const [rowId, title] of Object.entries(raw.titles)) {
      if (ID.test(rowId) && typeof title === 'string' && title.trim() !== '') titles[rowId.toLowerCase()] = title.trim().slice(0, 200);
    }
  }
  const saw: Record<string, string | null> = {};
  const seen = isObject(raw.saw) ? raw.saw : {};
  for (const fieldId of Object.keys(values)) saw[fieldId] = agentViewWords(seen[fieldId]) ?? null;
  const unseen = Array.isArray(raw.unseen) ? [...new Set(raw.unseen.filter((fieldId): fieldId is string => typeof fieldId === 'string' && fieldId in values))] : [];
  return {
    recordId: raw.recordId.toLowerCase(),
    values,
    titles,
    saw,
    ...(unseen.length === 0 ? {} : { unseen }),
    ...(lines === undefined ? {} : { lines }),
  };
};

/** The fill inside a `fill_entry` output, read loosely, or null. */
export const entryFillOf = (output: unknown): AgentEntryFill | null =>
  isObject(output) && output.kind === 'fill' ? readAgentEntryFill(output.fill) : null;
